import React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { getPortalRoot } from '../../../utils/portalRoot';
import type { ContactFormState } from '../hooks/useCartPage';

// Keyframes — embedded so they're always available
const contactOverlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const contactOverlayOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const contactDialogIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;
const contactDialogOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
`;
const contactSheetIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const contactSheetOut = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

// Styled Components — copied 1:1 from Repair CartPage.tsx
export const ContactModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$closing',
})<{ $closing?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  will-change: opacity;
  animation: ${({ $closing }) => $closing ? contactOverlayOut : contactOverlayIn} 0.3s ease forwards;

  @media (max-width: 640px) {
    align-items: flex-end;
  }
`;

export const ContactModalDialog = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$closing',
})<{ $closing?: boolean }>`
  background: #f8fafc;
  border-radius: 20px;
  max-width: 680px;
  width: 95%;
  height: auto;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  will-change: transform, opacity;
  animation: ${({ $closing }) => $closing ? contactDialogOut : contactDialogIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.45);
    color: #e5e7eb;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    border-radius: 20px 20px 0 0;
    flex-direction: column;
    max-height: 92vh;
    animation: ${({ $closing }) => $closing ? contactSheetOut : contactSheetIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

export const ContactModalBrandPane = styled.div`
  width: 38%;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  padding: 1.75rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

  * { font-family: inherit; }

  @media (max-width: 640px) {
    width: 100%;
    padding: 1.25rem 1.5rem 1rem;
  }
`;

export const ContactModalFormPane = styled.div`
  width: 62%;
  padding: 1.75rem;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

  * { font-family: inherit; }
  h3 { font-family: inherit; font-weight: 600; }
  p { font-family: inherit; }
  label { font-family: inherit; }
  input, textarea, button { font-family: inherit; }

  @media (max-width: 640px) {
    width: 100%;
    padding: 1.25rem 1.5rem 2rem;
  }
`;

export const CloseIconButton = styled.button`
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #475569;
    border-color: rgba(0, 0, 0, 0.15);
    transform: scale(1.05);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: rotate(90deg);
  }

  .dark & {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
    color: #94a3b8;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #cbd5e1;
      border-color: rgba(255, 255, 255, 0.2);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
  }
`;

export const ContactPreferenceLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  background: white;
  position: relative;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &:has(input:checked) {
    background: #f7f8fa;
    border-color: #4b5563;
  }

  &:has(input:checked) span {
    color: #111827;
  }
`;

export const Checkmark = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    opacity: 0;
    transition: opacity 0.2s ease;
    width: 10px;
    height: 10px;
  }

  input:checked + & {
    background: #4b5563;
    border-color: #4b5563;

    svg {
      opacity: 1;
    }
  }
`;

// Props interface
interface CartContactModalProps {
  isOpen: boolean;
  closing?: boolean;
  language: string;
  contactForm: ContactFormState;
  isSubmitting: boolean;
  showSuccess: boolean;
  t: (key: string) => string;
  onClose: () => void;
  onFormChange: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

// Inline input style — exact copy from Repair
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: '#ffffff'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '600',
  color: '#334155',
  marginBottom: '0.35rem'
};

const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = '#4b5563';
  e.target.style.outline = 'none';
  e.target.style.boxShadow = '0 0 0 3px rgba(75, 85, 99, 0.1)';
};

const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = '#e2e8f0';
  e.target.style.boxShadow = 'none';
};

const CartContactModal: React.FC<CartContactModalProps> = ({
  isOpen,
  closing = false,
  contactForm,
  isSubmitting,
  showSuccess,
  t,
  onClose,
  onFormChange,
  onSubmit
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  if (!isOpen) return null;

  return createPortal(
    <ContactModalOverlay $closing={closing} onClick={onClose}>
      <ContactModalDialog $closing={closing} onClick={(e) => e.stopPropagation()}>
        {/* Brand pane */}
        <ContactModalBrandPane>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
              <img src="/images/phonebroslogo.png" alt="Phone Bros Logo" style={{ height: '32px', filter: 'brightness(0) invert(1)' }} />
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Phone Bros</span>
            </div>
            <h2 style={{ fontSize: isMobile ? '1.15rem' : '1.5rem', fontWeight: 'bold', marginBottom: '0.625rem', lineHeight: 1.25 }}>
              {showSuccess
                ? t('messageSent')
                : t('contactFormIntro')
              }
            </h2>
            <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', opacity: 0.8, lineHeight: 1.5 }}>
              {showSuccess
                ? t('contactSoon')
                : t('humanContactText')
              }
            </p>
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>
            &copy; {new Date().getFullYear()} Phone Bros &mdash; {t('madeBy')} <a href="https://finemstudio.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', opacity: 0.9 }}>Finem Studio</a>
          </div>
        </ContactModalBrandPane>

        {/* Form pane */}
        <ContactModalFormPane>
          {showSuccess ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <svg style={{ width: '40px', height: '40px' }} fill="white" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.875rem' }}>
                {t('messageSentSuccessfully')}
              </h3>
              <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: '300px' }}>
                {t('thankYouMessage')}
              </p>
              <div style={{ background: 'rgba(239, 246, 255, 0.6)', padding: '1rem', borderRadius: '8px', border: '1px solid #dbeafe', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.875rem', color: '#1e3a8a' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" />
                </svg>
                {t('windowClosesAutomatically')}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h3 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                    {t('contactUs')}
                  </h3>
                </div>
                <CloseIconButton onClick={onClose} aria-label={t('close')}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </CloseIconButton>
              </div>

              <p style={{ color: '#475569', marginBottom: '1.25rem', lineHeight: 1.5, fontSize: '0.875rem' }}>
                {t('contactFormDescription')}
              </p>

              <div style={{ background: '#f7f8fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e6edf5', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <p style={{ color: '#111827', fontSize: '0.8rem', lineHeight: 1.5, margin: 0, fontWeight: 500 }}>
                  {t('languageSupportNoticeL1')} <br />
                  {t('languageSupportNoticeL2')}
                </p>
              </div>

              <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={labelStyle}>{t('name')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" required value={contactForm.name} onChange={(e) => onFormChange('name', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('fullNamePlaceholder')} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('email')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="email" required value={contactForm.email} onChange={(e) => onFormChange('email', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('emailPlaceholder')} />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>{t('phoneNumber')}</label>
                  <input type="tel" value={contactForm.phone} onChange={(e) => onFormChange('phone', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} placeholder={t('phonePlaceholder')} />
                </div>

                <div>
                  <label style={labelStyle}>{t('contactPreference')} <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <ContactPreferenceLabel>
                      <input type="radio" name="contactPreference" value="email" checked={contactForm.contactPreference === 'email'} onChange={(e) => onFormChange('contactPreference', e.target.value)} />
                      <Checkmark><svg viewBox="0 0 16 16" fill="white"><path d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z' /></svg></Checkmark>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('email')}</span>
                    </ContactPreferenceLabel>
                    <ContactPreferenceLabel>
                      <input type="radio" name="contactPreference" value="phone" checked={contactForm.contactPreference === 'phone'} onChange={(e) => onFormChange('contactPreference', e.target.value)} />
                      <Checkmark><svg viewBox="0 0 16 16" fill="white"><path d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z' /></svg></Checkmark>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('phone')}</span>
                    </ContactPreferenceLabel>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>{t('message')}</label>
                  <textarea
                    rows={3}
                    value={contactForm.message}
                    onChange={(e) => onFormChange('message', e.target.value)}
                    style={{ ...inputStyle, resize: 'vertical' as const, minHeight: '70px' }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={t('messagePlaceholder')}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', border: '1px solid #cbd5e1', background: 'white', color: '#334155', fontFamily: 'inherit' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: '600', fontSize: '0.875rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease',
                      border: '1px solid transparent',
                      background: isSubmitting ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' : '#374151',
                      color: 'white', fontFamily: 'inherit',
                      boxShadow: isSubmitting ? '0 4px 12px rgba(156, 163, 175, 0.2)' : '0 4px 12px rgba(79, 70, 229, 0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      opacity: isSubmitting ? 0.8 : 1
                    }}
                    onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)'; e.currentTarget.style.background = '#4b5563'; } }}
                    onMouseLeave={(e) => { if (!isSubmitting) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'; e.currentTarget.style.background = '#374151'; } }}
                  >
                    {isSubmitting && (
                      <svg style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {isSubmitting ? t('sending') : t('sendMessage')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </ContactModalFormPane>
      </ContactModalDialog>
    </ContactModalOverlay>,
    getPortalRoot()
  );
};

export default CartContactModal;
